/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Spinner,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { SEASONS } from 'common/src/utils/definitions';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import {
  AMBIGUOUS_SHIPMENT_REFERENCE_MESSAGE,
  getShipmentBoxesForContainer,
  getShipmentBoxesForReference,
} from 'common/src/api/shipments';
import {
  getExporterDivisions,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';
import { SettlementDetail } from 'common/src/api/excel-process';
import { ShipmentBox, Species } from 'common/src/utils/types';
import UploadSettlementDialog from './upload-settlement-dialog';
import { mapShipmentToSettlementDetail } from '../../utils/shipment-settlement-preview';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  type: 'manual' | 'shipments';
}

const ManualUploadModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  type,
}) => {
  const { userProfile } = useUserStore();
  const { selectedSeason: globalSeason } = useFilterStore();
  const { species, selectedSpecies: selectedSpeciesFromStore } = useDataStore();

  const [season, setSeason] = useState<string>(globalSeason || '');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(
    selectedSpeciesFromStore ?? null
  );
  const [exporterRef, setExporterRef] = useState<string>('');
  const [containerNumber, setContainerNumber] = useState<string>('');
  // A reference may exist once per division; multi-division exporters pick one.
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');
  const divisionId = selectedDivisionId ? Number(selectedDivisionId) : null;
  useEffect(() => {
    if (!isOpen || !userProfile?.exporter_id) return;
    getExporterDivisions(userProfile.exporter_id)
      .then(setDivisions)
      .catch((error) => console.error('Error loading divisions:', error));
  }, [isOpen, userProfile?.exporter_id]);
  const [loading, setLoading] = useState(false);
  const [processedData, setProcessedData] = useState<SettlementDetail[] | null>(
    null
  );
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  useEffect(() => {
    if (!season && globalSeason) {
      setSeason(globalSeason);
    }
  }, [globalSeason, season]);

  useEffect(() => {
    if (!selectedSpecies && selectedSpeciesFromStore) {
      setSelectedSpecies(selectedSpeciesFromStore);
    }
  }, [selectedSpeciesFromStore, selectedSpecies]);

  const shipmentLookupVersion = useRef(0);
  useEffect(() => {
    shipmentLookupVersion.current += 1;
    setLoading(false);
  }, [
    isOpen,
    season,
    selectedSpecies?.id,
    exporterRef,
    containerNumber,
    userProfile?.exporter_id,
  ]);

  const handleFetchShipments = async () => {
    if (!season || !selectedSpecies) {
      toast.error('Por favor completa temporada y especie');
      return;
    }
    if (!exporterRef && !containerNumber) {
      toast.error('Ingresa una referencia o un número de contenedor');
      return;
    }

    const version = ++shipmentLookupVersion.current;
    const isCurrent = () => version === shipmentLookupVersion.current;
    setLoading(true);
    try {
      let shipmentBoxes: ShipmentBox[] = [];
      if (exporterRef) {
        shipmentBoxes =
          (await getShipmentBoxesForReference(
            userProfile?.exporter_id as number,
            season,
            selectedSpecies.id,
            exporterRef,
            divisionId
          )) || [];
      } else {
        shipmentBoxes =
          (await getShipmentBoxesForContainer(
            userProfile?.exporter_id as number,
            season,
            selectedSpecies.id,
            containerNumber
          )) || [];
      }

      if (!isCurrent()) return;
      if (shipmentBoxes.length === 0) {
        toast.error(
          exporterRef
            ? 'No se encontraron despachos para la referencia ingresada'
            : 'No se encontraron despachos para el contenedor ingresado'
        );
        return;
      }

      const settlementDetails: SettlementDetail[] = shipmentBoxes.map((box) =>
        mapShipmentToSettlementDetail(box, userProfile?.exporter?.name || '')
      );

      // console.log('settlementDetails', settlementDetails);

      setProcessedData(settlementDetails);
      setIsUploadDialogOpen(true);
    } catch (error) {
      if (!isCurrent()) return;
      if (
        error instanceof Error &&
        error.message === AMBIGUOUS_SHIPMENT_REFERENCE_MESSAGE
      ) {
        toast.error(error.message);
        return;
      }
      console.error('Error obteniendo despachos:', error);
      toast.error('Error al obtener los despachos');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleManualUpload = async () => {
    if (!season || !selectedSpecies) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setLoading(true);
    try {
      setProcessedData([]);
      setIsUploadDialogOpen(true);
    } catch (error) {
      console.error('Error subiendo liquidación:', error);
      toast.error('Error al subir la liquidación');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSeason(globalSeason || '');
    setSelectedSpecies(null);
    setExporterRef('');
    setProcessedData(null);
    setIsUploadDialogOpen(false);
    onClose();
  };

  const handleUploadSuccess = () => {
    onSuccess();
    handleClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !isUploadDialogOpen}
        onClose={handleClose}
        size='lg'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <h3 className='text-xl font-bold'>
              Subir Liquidación{' '}
              {type === 'shipments'
                ? 'vía Planilla de Despachos'
                : 'vía Manual'}
            </h3>
            <p className='text-sm text-default-500'>
              {type === 'shipments'
                ? 'Completa la referencia o contenedor para buscar los despachos'
                : 'Completa la información para subir la liquidación'}
            </p>
          </ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <Select
                label='Temporada *'
                selectedKeys={[season]}
                onChange={(e) => setSeason(e.target.value)}
                className='w-full'
                size='sm'
              >
                {Object.values(SEASONS).map((s) => (
                  <SelectItem key={s.name} value={s.name}>
                    {s.name}
                  </SelectItem>
                ))}
              </Select>

              <Select
                label='Especie *'
                selectedKeys={
                  selectedSpecies ? [selectedSpecies.id.toString()] : []
                }
                onChange={(e) => {
                  const sp = species.find(
                    (sp) => sp.id.toString() === e.target.value
                  );
                  setSelectedSpecies(sp || null);
                }}
                className='w-full'
                size='sm'
              >
                {species.map((sp) => (
                  <SelectItem key={sp.id} value={sp.id.toString()}>
                    {sp.name}
                  </SelectItem>
                ))}
              </Select>

              {type === 'shipments' && divisions.length > 1 && (
                <Select
                  label='División'
                  selectedKeys={[selectedDivisionId || 'all']}
                  onChange={(e) =>
                    setSelectedDivisionId(
                      e.target.value === 'all' ? '' : e.target.value
                    )
                  }
                  description='Con "Todas", la referencia debe existir en una sola división.'
                >
                  {[
                    <SelectItem key='all' value='all'>
                      Todas
                    </SelectItem>,
                    ...divisions.map((division) => (
                      <SelectItem key={division.id} value={division.id}>
                        {division.name}
                      </SelectItem>
                    )),
                  ]}
                </Select>
              )}
              {type === 'shipments' && (
                <div className='flex flex-col md:flex-row items-end gap-4'>
                  <Input
                    className='flex-1'
                    label='Referencia Exportador'
                    value={exporterRef}
                    onChange={(e) => setExporterRef(e.target.value)}
                    placeholder='Ingresa la referencia (Ej: INV123)'
                  />
                  <h6 className='text-center self-center md:self-end pb-2'>
                    Ó
                  </h6>
                  <Input
                    className='flex-1'
                    label='Número de Contenedor'
                    value={containerNumber}
                    onChange={(e) => setContainerNumber(e.target.value)}
                    placeholder='Ej: HLBU-1234567'
                  />
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={
                type === 'shipments' ? handleFetchShipments : handleManualUpload
              }
              disabled={loading}
              color='primary'
              variant='solid'
            >
              {loading ? (
                <Spinner size='sm' color='default' />
              ) : type === 'shipments' ? (
                'Buscar Despachos'
              ) : (
                'Subir Liquidación'
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {processedData && (
        <UploadSettlementDialog
          isManualUpload={true}
          receiverNameSource={type === 'shipments' ? 'shipment' : 'settlement'}
          isOpen={isUploadDialogOpen}
          onClose={handleClose}
          excelData={processedData}
          onSuccess={handleUploadSuccess}
          selectedSpecies={selectedSpecies}
          defaultSeason={season}
          initialDivisionId={divisionId}
        />
      )}
    </>
  );
};

export default ManualUploadModal;
